// utils/FileUploader.js
import {uploadApi} from '@/api/uploadApi'

/**
 * 文件上传公共类
 * 支持大文件上传、断点续传、进度监控、多文件同时上传
 */
export class FileUploadUtil {
    /**
     * 构造函数
     * @param {Object} options 配置选项
     * @param {Function} options.onProgress 进度回调
     * @param {Function} options.onStatusChange 状态变化回调
     * @param {Function} options.onComplete 完成回调
     * @param {Function} options.onError 错误回调
     * @param {number} options.chunkSize 分片大小，默认5MB
     * @param {number} options.maxRetries 最大重试次数，默认3
     */
    constructor(options = {}) {
        this.options = {
            chunkSize: 5 * 1024 * 1024,
            maxRetries: 3,
            ...options
        }

        // 支持多个文件同时上传的状态管理
        this.uploads = new Map() // fileId -> uploadStatus
        this.nextFileId = 1
    }

    /**
     * 上传文件
     * @param {File} file 文件
     * @param {Object} initResponse 初始化响应
     * @returns {Promise}
     */
    async uploadFile(file, initResponse) {
        const uploadId = initResponse.uploadId

        const uploadStatus = {
            file: file,
            fileName: file.name,
            fileSize: file.size,
            uploadedSize: 0,
            progress: 0,
            speed: 0,
            remainingTime: 0,
            uploadId: initResponse.uploadId,
            chunks: [],
            paused: false,
            isProcessing: true
        }

        this.uploads.set(uploadId, uploadStatus);
        try {
            this._updateStatus(uploadId, 'start', {message: '开始上传文件'})

            if (!initResponse) {
                // 初始化上传
                initResponse = await uploadApi.initUpload({
                    fileName: file.name,
                    fileSize: file.size
                })
            }

            uploadStatus.uploadId = initResponse.uploadId
            uploadStatus.chunks = Array.from({length: initResponse.totalChunks}, (_, i) => ({
                number: i + 1,
                uploaded: false,
                uploading: false,
                failed: false
            }))
            console.log('initResponse-uploadStatus', uploadStatus)
            this._updateStatus(uploadId, 'initialized', {
                message: '上传已初始化',
                totalChunks: initResponse.totalChunks
            })

            // 获取已上传的分片
            const uploadedChunks = await uploadApi.getUploadedChunks(initResponse.uploadId)
            uploadedChunks.forEach(chunkNumber => {
                const chunk = uploadStatus.chunks.find(c => c.number === chunkNumber)
                if (chunk) {
                    chunk.uploaded = true
                    this._updateProgress(uploadId, chunkNumber, initResponse.chunkSize)
                }
            })

            // 开始上传分片
            await this._uploadChunks(uploadId)

        } catch (error) {
            this._handleError(uploadId, error)
            throw error
        } finally {
            uploadStatus.isProcessing = false
        }
    }

    /**
     * 暂停上传指定文件
     * @param {string} uploadId 文件ID
     */
    pauseUpload(uploadId) {
        const status = this.uploads.get(uploadId)
        if (status && status.uploadId && !status.paused) {
            status.paused = true
            this._updateStatus(uploadId, 'paused', {message: '上传已暂停'})
        }
    }

    /**
     * 继续上传指定文件
     * @param {string} uploadId 文件ID
     */
    resumeUpload(uploadId) {
        const status = this.uploads.get(uploadId)
        if (status && status.uploadId && status.paused) {
            status.paused = false
            this._updateStatus(uploadId, 'resumed', {message: '继续上传'})
            this._uploadChunks(uploadId)
        }
    }

    /**
     * 取消上传指定文件
     * @param {string} uploadId 文件ID
     */
    async cancelUpload(uploadId) {
        const status = this.uploads.get(uploadId)
        if (status && status.uploadId) {
            try {
                await uploadApi.cancelUpload({
                    uploadId: status.uploadId
                })
            } catch (error) {
                console.error('取消上传失败:', error)
            }
        }
        this.uploads.delete(uploadId)
        this._updateStatus(uploadId, 'cancelled', {message: '上传已取消'})
    }

    /**
     * 取消所有文件上传
     */
    async cancelAllUploads() {
        const cancelPromises = Array.from(this.uploads.keys()).map(uploadId =>
            this.cancelUpload(uploadId)
        )
        await Promise.allSettled(cancelPromises)
    }

    /**
     * 获取指定文件状态
     * @param {string} uploadId 文件ID
     * @returns {Object} 状态对象
     */
    getFileStatus(uploadId) {
        const status = this.uploads.get(uploadId)
        return status ? {...status} : null
    }

    /**
     * 获取所有文件状态
     * @returns {Object} 所有文件状态
     */
    getAllStatus() {
        const files = {}
        this.uploads.forEach((status, uploadId) => {
            files[uploadId] = {...status}
        })
        return files
    }

    /**
     * 销毁实例
     */
    destroy() {
        this.cancelAllUploads()
        this.uploads.clear()
    }

    // 私有方法

    /**
     * 上传分片
     */
    async _uploadChunks(fileUploadId) {
        const status = this.uploads.get(fileUploadId)
        if (!status || !status.uploadId || !status.file) return

        const {uploadId, file, chunks} = status
        const chunkSize = this.options.chunkSize
        const totalChunks = chunks.length

        for (let i = 0; i < totalChunks; i++) {
            if (status.paused) break

            const chunk = chunks[i]
            if (chunk.uploaded || chunk.uploading) continue

            chunk.uploading = true
            const startTime = Date.now()

            try {
                const chunkBlob = file.slice(i * chunkSize, (i + 1) * chunkSize)
                const formData = new FormData()
                formData.append('uploadId', uploadId)
                formData.append('chunkNumber', chunk.number.toString())
                formData.append('file', chunkBlob, file.name)

                await uploadApi.uploadChunk(formData)

                chunk.uploaded = true
                chunk.uploading = false
                this._updateProgress(uploadId, chunk.number, chunkBlob.size)

                // 计算上传速度
                const endTime = Date.now()
                const duration = (endTime - startTime) / 1000
                const speed = chunkBlob.size / duration
                status.speed = speed

                // 计算剩余时间
                const remainingSize = status.fileSize - status.uploadedSize
                status.remainingTime = remainingSize / speed

                this._updateStatus(uploadId, 'chunk_uploaded', {
                    chunkNumber: chunk.number,
                    speed,
                    remainingTime: status.remainingTime
                })

            } catch (error) {
                console.error(`文件 ${status.fileName} 分片 ${chunk.number} 上传失败:`, error)
                chunk.failed = true
                chunk.uploading = false

                // 重试机制
                await this._retryChunkUpload(uploadId, chunk, i)
            }
        }

        // 检查是否所有分片都上传完成
        const allUploaded = chunks.every(chunk => chunk.uploaded)
        if (allUploaded && !status.paused) {
            const result = await this._completeUpload(uploadId)
            this._updateStatus(uploadId, 'completed', {
                message: '文件上传成功',
                result
            })

            return result
        }
    }

    /**
     * 重试失败的分片
     */
    async _retryChunkUpload(uploadId, chunk, chunkIndex) {
        const status = this.uploads.get(uploadId)
        if (!status) return

        const maxRetries = this.options.maxRetries
        let retryCount = 0

        while (retryCount < maxRetries && !chunk.uploaded && !status.paused) {
            retryCount++

            try {
                const chunkBlob = status.file.slice(
                    chunkIndex * this.options.chunkSize,
                    (chunkIndex + 1) * this.options.chunkSize
                )
                const formData = new FormData()
                formData.append('uploadId', status.uploadId)
                formData.append('chunkNumber', chunk.number.toString())
                formData.append('file', chunkBlob, status.fileName)

                await uploadApi.uploadChunk(formData)

                chunk.uploaded = true
                chunk.uploading = false
                chunk.failed = false
                this._updateProgress(uploadId, chunk.number, chunkBlob.size)
                break

            } catch (error) {
                console.error(`文件 ${status.fileName} 分片 ${chunk.number} 第 ${retryCount} 次重试失败:`, error)
                if (retryCount === maxRetries) {
                    chunk.failed = true
                }
            }
        }
    }

    /**
     * 完成上传
     */
    async _completeUpload(uploadId) {
        const status = this.uploads.get(uploadId)
        if (!status || !status.uploadId) return

        try {
            const result = await uploadApi.completeUpload({
                uploadId: status.uploadId
            })

            this._updateStatus(uploadId, 'completing', {message: '正在完成上传'})
            return result

        } catch (error) {
            throw new Error('上传完成失败: ' + error.message)
        }
    }

    /**
     * 更新进度
     */
    _updateProgress(uploadId, chunkNumber, chunkSize) {
        const status = this.uploads.get(uploadId)
        if (!status) return

        status.uploadedSize += chunkSize
        status.progress = ((status.uploadedSize / status.fileSize) * 100).toFixed(2)
    }

    /**
     * 更新状态并触发回调
     */
    _updateStatus(uploadId, event, data = {}) {
        const status = this.uploads.get(uploadId)
        if (!status) return

        // 更新状态
        Object.assign(status, data)

        // 触发回调
        if (this.options.onStatusChange) {
            this.options.onStatusChange(uploadId, event, {...status, ...data})
        }

        // 特定事件回调
        if (event === 'completed' && this.options.onComplete) {
            // console.log('completed')
            this.options.onComplete(uploadId, status, data.result)
        } else if (event === 'error' && this.options.onError) {
            this.options.onError(uploadId, data.error)
        } else if (this.options.onProgress) {
            this.options.onProgress(uploadId, status)
        }
    }

    /**
     * 处理错误
     */
    _handleError(uploadId, error) {
        this._updateStatus(uploadId, 'error', {
            error,
            message: error.message
        })
    }

    /**
     * 工具函数 - 格式化文件大小
     */
    static formatFileSize(bytes) {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    /**
     * 工具函数 - 格式化速度
     */
    static formatSpeed(bytesPerSecond) {
        return this.formatFileSize(bytesPerSecond) + '/s'
    }

    /**
     * 工具函数 - 格式化时间
     */
    static formatTime(seconds) {
        if (seconds === 0) return '-'
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = Math.floor(seconds % 60)

        if (hours > 0) {
            return hours + 'h ' + minutes + 'm ' + secs + 's'
        } else if (minutes > 0) {
            return minutes + 'm ' + secs + 's'
        } else {
            return secs + 's'
        }
    }
}